'use client';

import {
  useContext,
  useCallback,
  type FC,
  useMemo,
  useState,
  useEffect,
} from 'react';
import type { SelectNewOwnerProps } from './SelectNewOwner.types';
import {
  Icon,
  Heading,
  Flex,
  InputChips,
  type ResultListItem,
  Alert,
} from '@/_components';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { User } from '@/_types/models/user';
import styles from './SelectNewOwner.module.css';
import { findUsers } from '@/_api/v2/admin-users';
import { UserContext } from '@/_contexts/UserProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import { isTestEmail } from '@/_utils/meta';

export const SelectNewOwner: FC<SelectNewOwnerProps> = ({
  onNewEntity,
  setNewOwners,
}) => {
  const { selectedCompany } = useContext(CompanyContext);
  const { token, isFull, isPartner } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [chips, setChips] = useState<Set<string>>(new Set());
  const [chipText, setChipText] = useState<string>('');
  const [users, setUsers] = useState<Array<User>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const debouncedSearch = useDebounce(chipText, 300);

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChipText(event.target.value);

  const fetchUsers = useCallback(
    async (search: string): Promise<void> => {
      if (!search || !token) return setUsers([]);

      setIsLoading(true);

      try {
        const users = await findUsers({ token, search });
        setUsers(users);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  const userItems: Array<ResultListItem> = useMemo(() => {
    return users
      ?.filter(
        (user) =>
          !user?.is_advisor &&
          (!user?.firm_group ||
            user?.firm_group?.id === selectedCompany?.user?.firm_group?.id),
      )
      ?.map((user) => {
        const hasNoEmail = isTestEmail(user.email);
        return {
          name: `${user.full_name} ${hasNoEmail ? '' : `(${user.email})`}`,
          value: user?.id?.toString() || '-1',
          label: hasNoEmail ? 'No email' : undefined,
          labelType: hasNoEmail ? ('warning' as const) : undefined,
        };
      })
      ?.sort((a, b) => a.name.localeCompare(b.name));
  }, [users, selectedCompany?.user?.firm_group?.id]);

  useEffect(() => {
    if (chips.size === 1) {
      const chipValue = chips.values().next().value;

      if (chipValue !== undefined) {
        const newUser: Partial<User> | undefined = users.find(
          (c) => c.id === parseInt(chipValue, 10),
        );

        const newOwners: Array<User> = [
          ...(selectedCompany?.all_owners || []),
          newUser as User,
        ];

        setNewOwners(newOwners);
      }
    }
  }, [chips]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUsers(debouncedSearch);
  }, [fetchUsers, debouncedSearch]);

  return (
    <>
      <Flex isColumn={true} gap={8}>
        {preferences?.has_firm_groups && (isFull || isPartner) && (
          <Alert type="info" icon="info">
            <b>Existing User:</b>
            <br />
            Clients in the same firm group as the selected entity, or clients
            not assigned to a firm group.
            <br />
            <br />
            <b>New User:</b>
            <br />
            Clients will be assigned to the selected entity&apos;s firm group.
          </Alert>
        )}
        <Flex isYCentered={true} gap={4} isFullWidth={true}>
          <Flex
            isColumn={true}
            isCentered={true}
            isFullWidth={true}
            className={styles.selectUser__chooseNewEntityItem}
          >
            <InputChips
              results={userItems}
              chips={chips}
              isLoading={isLoading}
              label="Existing User"
              onChange={onChipText}
              setChips={setChips}
              value={chipText}
              placeholder="Doe, John"
            />
          </Flex>
          <Flex
            isCentered={true}
            gap={2}
            onClick={onNewEntity}
            isFullWidth={true}
            className={styles.selectUser__chooseNewEntityItem}
          >
            <Icon size={24} color="accent" name="user-plus" />
            <Heading color="accent" level={6}>
              New User
            </Heading>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
